import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Mail, Phone, RefreshCw, Search, Eye, Edit2, Trash2, X, Check,
  Clock, Shield, LogOut, MessageSquare, Building2, CheckCircle2, AlertCircle,
  FileText, Newspaper, BarChart3, Plus, Save, Globe, EyeOff, Tag,
  Download, Filter, Calendar, TrendingUp, PhoneCall, MailOpen, Archive,
  ChevronLeft, ChevronRight, ArrowUpDown, Activity, Inbox, UserCheck,
  Copy, ExternalLink, FolderOpen, BookOpen, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { RichTextEditor } from '../components/admin/RichTextEditor';

const AdminPage = () => {
  // SEO - noindex for admin pages
  React.useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);
    return () => { document.head.removeChild(metaRobots); };
  }, []);

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [authHeader, setAuthHeader] = useState('');

  // Navigation
  const [activeTab, setActiveTab] = useState('dashboard');

  // Inquiries state
  const [inquiries, setInquiries] = useState([]);
  const [inquiryStats, setInquiryStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [investorTypeFilter, setInvestorTypeFilter] = useState('');
  const [investmentRangeFilter, setInvestmentRangeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryEditMode, setInquiryEditMode] = useState(false);
  const [inquiryEditData, setInquiryEditData] = useState({ status: '', notes: '' });
  const [selectedInquiries, setSelectedInquiries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // CMS state
  const [cmsStats, setCmsStats] = useState(null);
  const [contentItems, setContentItems] = useState([]);
  const [contentType, setContentType] = useState('insight');
  const [contentStatusFilter, setContentStatusFilter] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentForm, setShowContentForm] = useState(false);
  const [contentFormData, setContentFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    featured_image: '',
    author: '',
    status: 'draft',
    publish_date: '',
    // Type-specific fields
    case_study_data: null,
    market_report_data: null,
    resource_data: null
  });

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);

  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Content types configuration
  const contentTypes = [
    { value: 'insight', label: 'Insights', icon: FileText, description: 'Blog posts & articles' },
    { value: 'press', label: 'Press', icon: Newspaper, description: 'Press releases' },
    { value: 'market_report', label: 'Market Reports', icon: BarChart3, description: 'Market analysis' },
    { value: 'case_study', label: 'Case Studies', icon: Building2, description: 'Investment case studies' },
    { value: 'resource', label: 'Resources', icon: BookOpen, description: 'Investor resources' }
  ];

  // Status options for inquiries
  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Inbox },
    { value: 'contacted', label: 'Contacted', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: PhoneCall },
    { value: 'qualified', label: 'Qualified', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: UserCheck },
    { value: 'archived', label: 'Archived', color: 'bg-white/10 text-white/50 border-white/20', icon: Archive }
  ];

  // Content status options
  const contentStatusOptions = [
    { value: 'draft', label: 'Draft', color: 'bg-white/10 text-white/50' },
    { value: 'published', label: 'Published', color: 'bg-emerald-500/20 text-emerald-400' },
    { value: 'scheduled', label: 'Scheduled', color: 'bg-purple-500/20 text-purple-400' }
  ];

  // Category options by content type
  const categoryOptions = {
    insight: ['Market Analysis', 'Investment Strategy', 'Property Management', 'Fund Updates', 'Industry News'],
    press: ['Acquisition', 'Fund Launch', 'Company News', 'Partnership', 'Award'],
    market_report: ['Q1 Report', 'Q2 Report', 'Q3 Report', 'Q4 Report', 'Annual Report', 'Special Report'],
    case_study: ['Multifamily', 'Office', 'Retail', 'Industrial', 'Mixed-Use'],
    resource: ['Educational', 'Webinar', 'Guide', 'Template', 'Video']
  };

  // Investor type options
  const investorTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'accredited', label: 'Accredited Investor' },
    { value: 'family-office', label: 'Family Office' },
    { value: 'institutional', label: 'Institutional' },
    { value: 'broker-lender', label: 'Broker/Lender' },
    { value: 'other', label: 'Other' }
  ];

  // Investment range options
  const investmentRangeOptions = [
    { value: '', label: 'All Ranges' },
    { value: 'under-250k', label: 'Under $250K' },
    { value: '250k-500k', label: '$250K - $500K' },
    { value: '500k-1m', label: '$500K - $1M' },
    { value: '1m-plus', label: '$1M+' }
  ];

  // Date filter options
  const dateFilterOptions = [
    { value: '', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' }
  ];

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });

      if (response.ok) {
        setAuthHeader(`Basic ${auth}`);
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', auth);
      } else {
        setAuthError('Invalid credentials');
      }
    } catch (err) {
      setAuthError('Connection error. Please try again.');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    localStorage.removeItem('adminAuth');
    setCredentials({ username: '', password: '' });
  };

  // Check for saved auth
  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth) {
      setAuthHeader(`Basic ${savedAuth}`);
      setIsAuthenticated(true);
    }
  }, []);

  // Show success message
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Fetch inquiries
  const fetchInquiries = useCallback(async () => {
    if (!authHeader) return;
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiries`, { 
        headers: { 'Authorization': authHeader } 
      });
      if (response.status === 401) { handleLogout(); return; }
      if (!response.ok) throw new Error('Failed to fetch inquiries');
      
      const data = await response.json();
      setInquiries(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, authHeader]);

  // Fetch inquiry stats
  const fetchInquiryStats = useCallback(async () => {
    if (!authHeader) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': authHeader }
      });
      if (response.ok) {
        const data = await response.json();
        setInquiryStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [API_URL, authHeader]);

  // Fetch CMS stats
  const fetchCmsStats = useCallback(async () => {
    if (!authHeader) return;
    try {
      const response = await fetch(`${API_URL}/api/cms/stats`, {
        headers: { 'Authorization': authHeader }
      });
      if (response.ok) {
        const data = await response.json();
        setCmsStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch CMS stats:', err);
    }
  }, [API_URL, authHeader]);

  // Fetch content items
  const fetchContent = useCallback(async () => {
    if (!authHeader) return;
    setLoading(true);
    
    try {
      let url = `${API_URL}/api/cms/content?content_type=${contentType}`;
      if (contentStatusFilter) url += `&status=${contentStatusFilter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': authHeader }
      });
      if (response.status === 401) { handleLogout(); return; }
      if (!response.ok) throw new Error('Failed to fetch content');
      
      const data = await response.json();
      setContentItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, authHeader, contentType, contentStatusFilter]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated && authHeader) {
      fetchInquiries();
      fetchInquiryStats();
      fetchCmsStats();
    }
  }, [isAuthenticated, authHeader, fetchInquiries, fetchInquiryStats, fetchCmsStats]);

  // Fetch content when content type or filter changes
  useEffect(() => {
    if (isAuthenticated && authHeader && activeTab === 'cms') {
      fetchContent();
    }
  }, [isAuthenticated, authHeader, activeTab, contentType, contentStatusFilter, fetchContent]);

  // Filter and sort inquiries
  const filteredInquiries = useMemo(() => {
    let filtered = [...inquiries];

    if (statusFilter) filtered = filtered.filter(inq => inq.status === statusFilter);
    if (investorTypeFilter) filtered = filtered.filter(inq => inq.investor_type === investorTypeFilter);
    if (investmentRangeFilter) filtered = filtered.filter(inq => inq.investment_range === investmentRangeFilter);

    if (dateFilter) {
      const now = new Date();
      filtered = filtered.filter(inq => {
        const inqDate = new Date(inq.created_at);
        switch (dateFilter) {
          case 'today': return inqDate >= new Date(now.setHours(0, 0, 0, 0));
          case 'week': return inqDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          case 'month': return inqDate >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          case 'quarter': return inqDate >= new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          default: return true;
        }
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inq => 
        inq.first_name?.toLowerCase().includes(term) ||
        inq.last_name?.toLowerCase().includes(term) ||
        inq.email?.toLowerCase().includes(term) ||
        inq.phone?.includes(term)
      );
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [inquiries, statusFilter, investorTypeFilter, investmentRangeFilter, dateFilter, searchTerm, sortOrder]);

  // Paginated inquiries
  const paginatedInquiries = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredInquiries.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredInquiries, currentPage]);

  const totalPages = Math.ceil(filteredInquiries.length / itemsPerPage);

  // Update inquiry status
  const updateInquiryStatus = async (id, newStatus, notes = null) => {
    try {
      const body = { status: newStatus };
      if (notes !== null) body.notes = notes;
      
      const response = await fetch(`${API_URL}/api/admin/inquiries/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        fetchInquiries();
        fetchInquiryStats();
        if (selectedInquiry?.id === id) {
          const updated = await response.json();
          setSelectedInquiry(prev => ({ ...prev, ...updated }));
        }
        showSuccess('Inquiry updated successfully');
      }
    } catch (err) {
      console.error('Failed to update inquiry:', err);
    }
  };

  // Bulk update status
  const bulkUpdateStatus = async (newStatus) => {
    if (selectedInquiries.length === 0) return;
    for (const id of selectedInquiries) {
      await updateInquiryStatus(id, newStatus);
    }
    setSelectedInquiries([]);
  };

  // Delete inquiry
  const deleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiries/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': authHeader }
      });

      if (response.ok) {
        fetchInquiries();
        fetchInquiryStats();
        setSelectedInquiry(null);
        showSuccess('Inquiry deleted');
      }
    } catch (err) {
      console.error('Failed to delete inquiry:', err);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Investor Type', 'Investment Range', 'Status', 'Date'];
    const rows = filteredInquiries.map(inq => [
      `${inq.first_name} ${inq.last_name}`,
      inq.email,
      inq.phone || '',
      inq.investor_type || '',
      inq.investment_range || '',
      inq.status,
      new Date(inq.created_at).toLocaleDateString()
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inquiries_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Toggle select inquiry
  const toggleSelectInquiry = (id) => {
    setSelectedInquiries(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Select all inquiries on current page
  const toggleSelectAll = () => {
    if (selectedInquiries.length === paginatedInquiries.length) {
      setSelectedInquiries([]);
    } else {
      setSelectedInquiries(paginatedInquiries.map(inq => inq.id));
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);

      const response = await fetch(`${API_URL}/api/media/upload-file`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setContentFormData(prev => ({
          ...prev,
          featured_image: data.file_url
        }));
        showSuccess('Image uploaded successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Initialize content form for new or edit
  const initContentForm = (content = null) => {
    if (content) {
      setContentFormData({
        ...content,
        tags: content.tags || [],
        case_study_data: content.case_study_data || null,
        market_report_data: content.market_report_data || null,
        resource_data: content.resource_data || null
      });
      setSelectedContent(content);
    } else {
      setContentFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: '',
        tags: [],
        featured_image: '',
        author: 'ASAP Capital',
        status: 'draft',
        publish_date: '',
        case_study_data: contentType === 'case_study' ? {
          property_name: '',
          location: '',
          asset_type: '',
          investment_thesis: '',
          key_improvements: [],
          before_metrics: {},
          after_metrics: {}
        } : null,
        market_report_data: contentType === 'market_report' ? {
          report_type: '',
          report_period: '',
          pdf_url: ''
        } : null,
        resource_data: contentType === 'resource' ? {
          resource_format: '',
          file_url: '',
          is_gated: false,
          cta_button_label: '',
          cta_button_link: ''
        } : null
      });
      setSelectedContent(null);
    }
    setShowContentForm(true);
  };

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Handle tag input
  const handleTagInput = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value && !contentFormData.tags.includes(value)) {
        setContentFormData(prev => ({
          ...prev,
          tags: [...prev.tags, value]
        }));
      }
      e.target.value = '';
    }
  };

  // Remove tag
  const removeTag = (tagToRemove) => {
    setContentFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Save content
  const saveContent = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const slug = contentFormData.slug || generateSlug(contentFormData.title);
      
      const payload = {
        ...contentFormData,
        slug,
        content_type: contentType
      };

      // Remove null type-specific data
      if (!payload.case_study_data) delete payload.case_study_data;
      if (!payload.market_report_data) delete payload.market_report_data;
      if (!payload.resource_data) delete payload.resource_data;

      const url = selectedContent 
        ? `${API_URL}/api/cms/content/${selectedContent.id}`
        : `${API_URL}/api/cms/content`;
      
      const response = await fetch(url, {
        method: selectedContent ? 'PATCH' : 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchContent();
        fetchCmsStats();
        setShowContentForm(false);
        setSelectedContent(null);
        showSuccess(selectedContent ? 'Content updated successfully' : 'Content created successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save content');
      }
    } catch (err) {
      console.error('Failed to save content:', err);
      setError('Failed to save content');
    } finally {
      setLoading(false);
    }
  };

  // Delete content
  const deleteContent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/cms/content/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': authHeader }
      });

      if (response.ok) {
        fetchContent();
        fetchCmsStats();
        showSuccess('Content deleted');
      }
    } catch (err) {
      console.error('Failed to delete content:', err);
    }
  };

  // Duplicate content
  const duplicateContent = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/cms/content/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': authHeader }
      });

      if (response.ok) {
        fetchContent();
        fetchCmsStats();
        showSuccess('Content duplicated');
      }
    } catch (err) {
      console.error('Failed to duplicate content:', err);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  // Get status badge
  const getInquiryStatusBadge = (status) => {
    const option = statusOptions.find(o => o.value === status) || statusOptions[0];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${option.color}`}>
        <option.icon className="h-3 w-3" />
        {option.label}
      </span>
    );
  };

  // Get content status badge
  const getContentStatusBadge = (status) => {
    const option = contentStatusOptions.find(o => o.value === status) || contentStatusOptions[0];
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${option.color}`}>
        {option.label}
      </span>
    );
  };

  // ==================== LOGIN SCREEN ====================
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Admin Login | ASAP Capital</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 shadow-2xl">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-[#0A1628]" />
                </div>
                <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
                <p className="text-white/50 mt-2">ASAP Capital Management</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Username</label>
                  <input
                    type="text"
                    value={credentials.username}
                    onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#D4AF37]/50"
                    placeholder="Enter username"
                    required
                    data-testid="admin-username"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Password</label>
                  <input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#D4AF37]/50"
                    placeholder="Enter password"
                    required
                    data-testid="admin-password"
                  />
                </div>

                {authError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {authError}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold py-3"
                  data-testid="admin-login-btn"
                >
                  Sign In
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  // ==================== MAIN ADMIN INTERFACE ====================
  return (
    <>
      <Helmet>
        <title>Admin Dashboard | ASAP Capital</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        {/* Success Message Toast */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="fixed top-4 right-4 z-[100] bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-lg flex items-center gap-2 shadow-lg"
            >
              <CheckCircle2 className="h-5 w-5" />
              {successMessage}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <header className="bg-[#111D35] border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-lg flex items-center justify-center">
                  <Shield className="h-5 w-5 text-[#0A1628]" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">ASAP Capital</h1>
                  <p className="text-xs text-white/50">Admin Dashboard</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  onClick={() => { fetchInquiries(); fetchInquiryStats(); fetchCmsStats(); fetchContent(); }}
                  variant="ghost"
                  size="sm"
                  className="text-white/60 hover:text-white"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="sm"
                  className="text-white/60 hover:text-red-400"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Navigation Tabs */}
        <div className="bg-[#0d1a2d] border-b border-white/5">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                { id: 'inquiries', label: 'Inquiries', icon: Users },
                { id: 'cms', label: 'Content', icon: FileText }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'text-[#D4AF37] border-[#D4AF37]'
                      : 'text-white/50 border-transparent hover:text-white/70'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* ==================== DASHBOARD TAB ==================== */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Inquiry Stats */}
              <div>
                <h2 className="text-lg font-semibold text-white mb-4">Inquiries Overview</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Inbox className="h-5 w-5 text-blue-400" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-white">{inquiryStats?.total || 0}</p>
                    <p className="text-sm text-white/50 mt-1">Total Inquiries</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-blue-500/20"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <AlertCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">New</span>
                    </div>
                    <p className="text-3xl font-bold text-blue-400">{inquiryStats?.new || 0}</p>
                    <p className="text-sm text-white/50 mt-1">Awaiting Response</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-amber-500/20"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <PhoneCall className="h-5 w-5 text-amber-400" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-amber-400">{inquiryStats?.contacted || 0}</p>
                    <p className="text-sm text-white/50 mt-1">Contacted</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-emerald-500/20"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <UserCheck className="h-5 w-5 text-emerald-400" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-emerald-400">{inquiryStats?.qualified || 0}</p>
                    <p className="text-sm text-white/50 mt-1">Qualified Leads</p>
                  </motion.div>
                </div>
              </div>

              {/* CMS Stats */}
              <div>
                <h2 className="text-lg font-semibold text-white mb-4">Content Overview</h2>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                  {contentTypes.map((type, index) => (
                    <motion.div
                      key={type.value}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-4 border border-white/10 hover:border-[#D4AF37]/30 transition-colors cursor-pointer"
                      onClick={() => { setActiveTab('cms'); setContentType(type.value); }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                          <type.icon className="h-4 w-4 text-[#D4AF37]" />
                        </div>
                        <span className="text-white font-medium text-sm">{type.label}</span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-2xl font-bold text-white">{cmsStats?.[type.value]?.total || 0}</p>
                          <p className="text-xs text-white/40">Total</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-emerald-400">{cmsStats?.[type.value]?.published || 0}</p>
                          <p className="text-xs text-white/40">Published</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Recent Inquiries */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10"
                >
                  <div className="p-5 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Activity className="h-5 w-5 text-[#D4AF37]" />
                      <h3 className="font-semibold text-white">Recent Inquiries</h3>
                    </div>
                    <button 
                      onClick={() => setActiveTab('inquiries')}
                      className="text-xs text-[#D4AF37] hover:underline"
                    >
                      View All
                    </button>
                  </div>
                  <div className="divide-y divide-white/5">
                    {inquiries.slice(0, 5).map((inq) => (
                      <div key={inq.id} className="p-4 hover:bg-white/5 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{inq.first_name} {inq.last_name}</p>
                            <p className="text-white/50 text-sm">{inq.email}</p>
                          </div>
                          <div className="text-right">
                            {getInquiryStatusBadge(inq.status)}
                            <p className="text-white/40 text-xs mt-1">{formatTimeAgo(inq.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {inquiries.length === 0 && (
                      <div className="p-8 text-center text-white/40">
                        <Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No inquiries yet</p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Recent Content */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10"
                >
                  <div className="p-5 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-[#D4AF37]" />
                      <h3 className="font-semibold text-white">Recent Content</h3>
                    </div>
                    <button 
                      onClick={() => setActiveTab('cms')}
                      className="text-xs text-[#D4AF37] hover:underline"
                    >
                      View All
                    </button>
                  </div>
                  <div className="divide-y divide-white/5">
                    {contentItems.slice(0, 5).map((item) => (
                      <div key={item.id} className="p-4 hover:bg-white/5 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{item.title}</p>
                            <p className="text-white/50 text-xs capitalize">{item.content_type.replace('_', ' ')}</p>
                          </div>
                          <div className="text-right ml-3">
                            {getContentStatusBadge(item.status)}
                            <p className="text-white/40 text-xs mt-1">{formatTimeAgo(item.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {contentItems.length === 0 && (
                      <div className="p-8 text-center text-white/40">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No content yet</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          )}

          {/* ==================== INQUIRIES TAB ==================== */}
          {activeTab === 'inquiries' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-4 border border-white/10">
                <div className="flex flex-wrap gap-3">
                  {/* Search */}
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <input
                      type="text"
                      placeholder="Search by name, email, phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                      data-testid="inquiry-search"
                    />
                  </div>

                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="admin-select px-3 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                    data-testid="status-filter"
                  >
                    <option value="">All Status</option>
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {/* Investor Type Filter */}
                  <select
                    value={investorTypeFilter}
                    onChange={(e) => setInvestorTypeFilter(e.target.value)}
                    className="admin-select px-3 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                  >
                    {investorTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {/* Date Filter */}
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="admin-select px-3 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                  >
                    {dateFilterOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {/* Sort */}
                  <button
                    onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-sm hover:bg-white/10"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
                  </button>

                  {/* Export */}
                  <Button
                    onClick={exportToCSV}
                    variant="outline"
                    size="sm"
                    className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    data-testid="export-csv-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>

                {/* Results count */}
                <div className="mt-3 flex items-center justify-between text-sm text-white/50">
                  <span>Showing {filteredInquiries.length} of {inquiries.length} inquiries</span>
                  {selectedInquiries.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span>{selectedInquiries.length} selected</span>
                      <div className="flex gap-1">
                        {statusOptions.slice(0, 3).map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => bulkUpdateStatus(opt.value)}
                            className={`px-2 py-1 rounded text-xs ${opt.color} hover:opacity-80`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Inquiries Table */}
              <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedInquiries.length === paginatedInquiries.length && paginatedInquiries.length > 0}
                            onChange={toggleSelectAll}
                            className="rounded border-white/20"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">Contact</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">Range</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-white/60 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {paginatedInquiries.map((inquiry) => (
                        <tr 
                          key={inquiry.id} 
                          className={`hover:bg-white/5 transition-colors cursor-pointer ${selectedInquiries.includes(inquiry.id) ? 'bg-[#D4AF37]/5' : ''}`}
                          onClick={() => setSelectedInquiry(inquiry)}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedInquiries.includes(inquiry.id)}
                              onChange={() => toggleSelectInquiry(inquiry.id)}
                              className="rounded border-white/20"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-white font-medium">{inquiry.first_name} {inquiry.last_name}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-white/70 text-sm">{inquiry.email}</p>
                            {inquiry.phone && <p className="text-white/50 text-xs">{inquiry.phone}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white/60 text-sm capitalize">{inquiry.investor_type?.replace('-', ' ') || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white/60 text-sm">{inquiry.investment_range || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            {getInquiryStatusBadge(inquiry.status)}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-white/50 text-sm">{formatDate(inquiry.created_at)}</p>
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {inquiry.status === 'new' && (
                                <button
                                  onClick={() => updateInquiryStatus(inquiry.id, 'contacted')}
                                  className="p-1.5 rounded hover:bg-amber-500/20 text-amber-400"
                                  title="Mark as Contacted"
                                >
                                  <PhoneCall className="h-4 w-4" />
                                </button>
                              )}
                              <a
                                href={`mailto:${inquiry.email}`}
                                className="p-1.5 rounded hover:bg-white/10 text-white/60"
                                title="Send Email"
                              >
                                <Mail className="h-4 w-4" />
                              </a>
                              <button
                                onClick={() => deleteInquiry(inquiry.id)}
                                className="p-1.5 rounded hover:bg-red-500/20 text-red-400"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between">
                    <p className="text-sm text-white/50">Page {currentPage} of {totalPages}</p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        variant="ghost"
                        size="sm"
                        className="text-white/60"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        variant="ghost"
                        size="sm"
                        className="text-white/60"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {paginatedInquiries.length === 0 && (
                  <div className="p-12 text-center text-white/40">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No inquiries found</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== CMS TAB ==================== */}
          {activeTab === 'cms' && (
            <div className="space-y-4">
              {/* Content Type Tabs & Actions */}
              <div className="flex flex-wrap items-center gap-3">
                {contentTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setContentType(type.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      contentType === type.value
                        ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30'
                        : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                    }`}
                    data-testid={`content-type-${type.value}`}
                  >
                    <type.icon className="h-4 w-4" />
                    {type.label}
                    {cmsStats?.[type.value]?.total > 0 && (
                      <span className="bg-white/10 px-1.5 py-0.5 rounded text-xs">{cmsStats[type.value].total}</span>
                    )}
                  </button>
                ))}
                
                <div className="ml-auto flex items-center gap-2">
                  {/* Status Filter */}
                  <select
                    value={contentStatusFilter}
                    onChange={(e) => setContentStatusFilter(e.target.value)}
                    className="admin-select px-3 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                  >
                    <option value="">All Status</option>
                    {contentStatusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  
                  <Button
                    onClick={() => initContentForm()}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628]"
                    data-testid="new-content-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New {contentTypes.find(t => t.value === contentType)?.label.slice(0, -1) || 'Content'}
                  </Button>
                </div>
              </div>

              {/* Content Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contentItems.map(item => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden group hover:border-[#D4AF37]/30 transition-colors"
                  >
                    {/* Featured Image */}
                    {item.featured_image ? (
                      <div className="relative h-40 bg-white/5">
                        <img src={item.featured_image} alt={item.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] to-transparent" />
                      </div>
                    ) : (
                      <div className="h-24 bg-gradient-to-br from-[#D4AF37]/10 to-transparent flex items-center justify-center">
                        {contentTypes.find(t => t.value === item.content_type)?.icon && 
                          React.createElement(contentTypes.find(t => t.value === item.content_type).icon, {
                            className: "h-8 w-8 text-[#D4AF37]/30"
                          })
                        }
                      </div>
                    )}
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-white font-semibold line-clamp-2 flex-1">{item.title}</h3>
                        {getContentStatusBadge(item.status)}
                      </div>
                      
                      <p className="text-white/50 text-sm line-clamp-2 mb-3">{item.excerpt || 'No excerpt'}</p>
                      
                      <div className="flex items-center justify-between text-xs text-white/40">
                        <span>{item.category || 'Uncategorized'}</span>
                        <span>{formatDate(item.publish_date || item.created_at)}</span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-white/5">
                        <button
                          onClick={() => initContentForm(item)}
                          className="flex-1 flex items-center justify-center gap-1 p-2 rounded-lg hover:bg-white/10 text-white/60 text-sm transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => duplicateContent(item.id)}
                          className="p-2 rounded-lg hover:bg-white/10 text-white/60 transition-colors"
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        {item.status === 'published' && (
                          <a
                            href={`/${item.content_type === 'insight' ? 'insights' : item.content_type.replace('_', '-') + 's'}/${item.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg hover:bg-white/10 text-white/60 transition-colors"
                            title="View"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        <button
                          onClick={() => deleteContent(item.id)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {contentItems.length === 0 && (
                  <div className="col-span-full p-12 text-center text-white/40 bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10">
                    <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No {contentTypes.find(t => t.value === contentType)?.label.toLowerCase() || 'content'} yet</p>
                    <button
                      onClick={() => initContentForm()}
                      className="text-[#D4AF37] hover:underline text-sm"
                    >
                      Create your first {contentTypes.find(t => t.value === contentType)?.label.slice(0, -1).toLowerCase() || 'content'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>

        {/* ==================== INQUIRY DETAIL MODAL ==================== */}
        <AnimatePresence>
          {selectedInquiry && !showContentForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedInquiry(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl w-full max-w-lg border border-white/10 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-5 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedInquiry.first_name} {selectedInquiry.last_name}</h2>
                    <p className="text-white/50 text-sm">{formatDate(selectedInquiry.created_at)}</p>
                  </div>
                  <button onClick={() => setSelectedInquiry(null)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="h-5 w-5 text-white/60" />
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                      <Mail className="h-5 w-5 text-[#D4AF37]" />
                      <div>
                        <p className="text-white/40 text-xs">Email</p>
                        <a href={`mailto:${selectedInquiry.email}`} className="text-white hover:text-[#D4AF37] text-sm">{selectedInquiry.email}</a>
                      </div>
                    </div>
                    {selectedInquiry.phone && (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                        <Phone className="h-5 w-5 text-[#D4AF37]" />
                        <div>
                          <p className="text-white/40 text-xs">Phone</p>
                          <a href={`tel:${selectedInquiry.phone}`} className="text-white hover:text-[#D4AF37] text-sm">{selectedInquiry.phone}</a>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-white/40 text-xs mb-1">Investor Type</p>
                      <p className="text-white font-medium capitalize">{selectedInquiry.investor_type?.replace('-', ' ') || 'Not specified'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-white/40 text-xs mb-1">Investment Range</p>
                      <p className="text-white font-medium">{selectedInquiry.investment_range || 'Not specified'}</p>
                    </div>
                  </div>

                  {/* Status Update */}
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-white/40 text-xs mb-2">Status</p>
                    <div className="flex flex-wrap gap-2">
                      {statusOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => updateInquiryStatus(selectedInquiry.id, opt.value)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            selectedInquiry.status === opt.value
                              ? opt.color
                              : 'border-white/10 text-white/50 hover:bg-white/5'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <p className="text-white/40 text-xs mb-2">Internal Notes</p>
                    {inquiryEditMode ? (
                      <div className="space-y-2">
                        <textarea
                          value={inquiryEditData.notes}
                          onChange={(e) => setInquiryEditData({ ...inquiryEditData, notes: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50 min-h-[100px]"
                          placeholder="Add notes..."
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              updateInquiryStatus(selectedInquiry.id, selectedInquiry.status, inquiryEditData.notes);
                              setInquiryEditMode(false);
                            }}
                            size="sm"
                            className="bg-[#D4AF37] text-[#0A1628]"
                          >
                            <Save className="h-4 w-4 mr-1" />
                            Save
                          </Button>
                          <Button
                            onClick={() => setInquiryEditMode(false)}
                            size="sm"
                            variant="ghost"
                            className="text-white/60"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        onClick={() => {
                          setInquiryEditMode(true);
                          setInquiryEditData({ status: selectedInquiry.status, notes: selectedInquiry.notes || '' });
                        }}
                        className="p-3 rounded-lg bg-white/5 border border-white/10 min-h-[80px] cursor-pointer hover:border-[#D4AF37]/30 transition-colors"
                      >
                        <p className="text-white/60 text-sm whitespace-pre-wrap">
                          {selectedInquiry.notes || 'Click to add notes...'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-5 border-t border-white/10 flex gap-2">
                  <a
                    href={`mailto:${selectedInquiry.email}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </a>
                  {selectedInquiry.phone && (
                    <a
                      href={`tel:${selectedInquiry.phone}`}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </a>
                  )}
                  <button
                    onClick={() => deleteInquiry(selectedInquiry.id)}
                    className="px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ==================== CONTENT FORM MODAL ==================== */}
        <AnimatePresence>
          {showContentForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
              onClick={() => setShowContentForm(false)}
            >
              <div className="min-h-screen flex items-start justify-center p-4 py-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl w-full max-w-4xl border border-white/10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-5 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#111D35]/95 backdrop-blur-sm rounded-t-2xl z-10">
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        {selectedContent ? 'Edit' : 'New'} {contentTypes.find(t => t.value === contentType)?.label.slice(0, -1) || 'Content'}
                      </h2>
                      <p className="text-white/50 text-sm">{contentTypes.find(t => t.value === contentType)?.description}</p>
                    </div>
                    <button onClick={() => setShowContentForm(false)} className="p-2 hover:bg-white/10 rounded-lg">
                      <X className="h-5 w-5 text-white/60" />
                    </button>
                  </div>

                  <form onSubmit={saveContent} className="p-5 space-y-6">
                    {/* Basic Fields */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-white/70 text-sm mb-2">Title *</label>
                        <input
                          type="text"
                          value={contentFormData.title}
                          onChange={(e) => {
                            setContentFormData({ 
                              ...contentFormData, 
                              title: e.target.value,
                              slug: contentFormData.slug || generateSlug(e.target.value)
                            });
                          }}
                          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#D4AF37]/50"
                          placeholder="Enter title"
                          required
                          data-testid="content-title"
                        />
                      </div>

                      <div>
                        <label className="block text-white/70 text-sm mb-2">URL Slug</label>
                        <input
                          type="text"
                          value={contentFormData.slug}
                          onChange={(e) => setContentFormData({ ...contentFormData, slug: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                          placeholder="auto-generated-from-title"
                          data-testid="content-slug"
                        />
                      </div>

                      <div>
                        <label className="block text-white/70 text-sm mb-2">Category</label>
                        <select
                          value={contentFormData.category}
                          onChange={(e) => setContentFormData({ ...contentFormData, category: e.target.value })}
                          className="admin-select w-full px-4 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                        >
                          <option value="">Select category</option>
                          {categoryOptions[contentType]?.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Excerpt */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Excerpt</label>
                      <textarea
                        value={contentFormData.excerpt}
                        onChange={(e) => setContentFormData({ ...contentFormData, excerpt: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                        placeholder="Brief description for previews..."
                        data-testid="content-excerpt"
                      />
                    </div>

                    {/* Rich Text Content */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Content *</label>
                      <RichTextEditor
                        content={contentFormData.content}
                        onChange={(html) => setContentFormData({ ...contentFormData, content: html })}
                        placeholder="Start writing your content..."
                      />
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Tags</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {contentFormData.tags.map(tag => (
                          <span 
                            key={tag} 
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs"
                          >
                            {tag}
                            <button type="button" onClick={() => removeTag(tag)} className="hover:text-white">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="text"
                        onKeyDown={handleTagInput}
                        className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                        placeholder="Type and press Enter to add tags..."
                      />
                    </div>

                    {/* Featured Image Upload */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        Featured Image
                        <span className="text-white/40 ml-2 text-xs font-normal">Recommended: 1200×630px (16:9 ratio), max 5MB</span>
                      </label>
                      
                      {/* Preview existing image */}
                      {contentFormData.featured_image && (
                        <div className="mb-3 relative group">
                          <img 
                            src={contentFormData.featured_image} 
                            alt="Featured preview" 
                            className="w-full max-h-48 object-cover rounded-lg border border-white/10"
                          />
                          <button
                            type="button"
                            onClick={() => setContentFormData({ ...contentFormData, featured_image: '' })}
                            className="absolute top-2 right-2 p-1.5 bg-red-500/80 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}

                      {/* Upload input */}
                      <div className="flex gap-3">
                        <label className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white/5 border border-white/10 border-dashed hover:border-[#D4AF37]/50 hover:bg-white/10 transition-colors">
                            <ImageIcon className="h-5 w-5 text-[#D4AF37]" />
                            <span className="text-white/70 text-sm">
                              {uploadingImage ? 'Uploading...' : 'Click to upload image'}
                            </span>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            disabled={uploadingImage}
                          />
                        </label>
                      </div>
                      
                      {/* Or use URL */}
                      <div className="mt-2">
                        <div className="flex items-center gap-2 text-white/40 text-xs mb-2">
                          <div className="flex-1 h-px bg-white/10"></div>
                          <span>or paste image URL</span>
                          <div className="flex-1 h-px bg-white/10"></div>
                        </div>
                        <input
                          type="url"
                          value={contentFormData.featured_image?.startsWith('data:') ? '' : contentFormData.featured_image}
                          onChange={(e) => setContentFormData({ ...contentFormData, featured_image: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </div>

                    {/* Author */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Author</label>
                        <input
                          type="text"
                          value={contentFormData.author}
                          onChange={(e) => setContentFormData({ ...contentFormData, author: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                          placeholder="Author name"
                        />
                      </div>
                    </div>

                    {/* Status & Publish Date */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Status</label>
                        <select
                          value={contentFormData.status}
                          onChange={(e) => setContentFormData({ ...contentFormData, status: e.target.value })}
                          className="admin-select w-full px-4 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                          data-testid="content-status"
                        >
                          {contentStatusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Publish Date</label>
                        <input
                          type="datetime-local"
                          value={contentFormData.publish_date ? contentFormData.publish_date.slice(0, 16) : ''}
                          onChange={(e) => setContentFormData({ ...contentFormData, publish_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                        />
                      </div>
                    </div>

                    {/* Case Study Specific Fields */}
                    {contentType === 'case_study' && (
                      <div className="border-t border-white/10 pt-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-[#D4AF37]" />
                          Case Study Details
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/70 text-sm mb-2">Property Name</label>
                            <input
                              type="text"
                              value={contentFormData.case_study_data?.property_name || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                case_study_data: { ...contentFormData.case_study_data, property_name: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                          <div>
                            <label className="block text-white/70 text-sm mb-2">Location</label>
                            <input
                              type="text"
                              value={contentFormData.case_study_data?.location || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                case_study_data: { ...contentFormData.case_study_data, location: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                          <div>
                            <label className="block text-white/70 text-sm mb-2">Asset Type</label>
                            <input
                              type="text"
                              value={contentFormData.case_study_data?.asset_type || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                case_study_data: { ...contentFormData.case_study_data, asset_type: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-white/70 text-sm mb-2">Investment Thesis</label>
                            <textarea
                              value={contentFormData.case_study_data?.investment_thesis || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                case_study_data: { ...contentFormData.case_study_data, investment_thesis: e.target.value }
                              })}
                              rows={2}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Market Report Specific Fields */}
                    {contentType === 'market_report' && (
                      <div className="border-t border-white/10 pt-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                          <BarChart3 className="h-5 w-5 text-[#D4AF37]" />
                          Report Details
                        </h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-white/70 text-sm mb-2">Report Type</label>
                            <select
                              value={contentFormData.market_report_data?.report_type || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                market_report_data: { ...contentFormData.market_report_data, report_type: e.target.value }
                              })}
                              className="admin-select w-full px-4 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            >
                              <option value="">Select type</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="annual">Annual</option>
                              <option value="special">Special Report</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/70 text-sm mb-2">Report Period</label>
                            <input
                              type="text"
                              value={contentFormData.market_report_data?.report_period || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                market_report_data: { ...contentFormData.market_report_data, report_period: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                              placeholder="e.g., Q4 2025"
                            />
                          </div>
                          <div>
                            <label className="block text-white/70 text-sm mb-2">PDF URL</label>
                            <input
                              type="url"
                              value={contentFormData.market_report_data?.pdf_url || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                market_report_data: { ...contentFormData.market_report_data, pdf_url: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Resource Specific Fields */}
                    {contentType === 'resource' && (
                      <div className="border-t border-white/10 pt-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                          <BookOpen className="h-5 w-5 text-[#D4AF37]" />
                          Resource Details
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/70 text-sm mb-2">Format</label>
                            <select
                              value={contentFormData.resource_data?.resource_format || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                resource_data: { ...contentFormData.resource_data, resource_format: e.target.value }
                              })}
                              className="admin-select w-full px-4 py-2 rounded-lg bg-[#111D35] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            >
                              <option value="">Select format</option>
                              <option value="pdf">PDF</option>
                              <option value="video">Video</option>
                              <option value="article">Article</option>
                              <option value="webinar">Webinar</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/70 text-sm mb-2">File/External URL</label>
                            <input
                              type="url"
                              value={contentFormData.resource_data?.file_url || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                resource_data: { ...contentFormData.resource_data, file_url: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                          <div>
                            <label className="block text-white/70 text-sm mb-2">CTA Button Label</label>
                            <input
                              type="text"
                              value={contentFormData.resource_data?.cta_button_label || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                resource_data: { ...contentFormData.resource_data, cta_button_label: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                          <div>
                            <label className="block text-white/70 text-sm mb-2">CTA Button Link</label>
                            <input
                              type="text"
                              value={contentFormData.resource_data?.cta_button_link || ''}
                              onChange={(e) => setContentFormData({ 
                                ...contentFormData, 
                                resource_data: { ...contentFormData.resource_data, cta_button_link: e.target.value }
                              })}
                              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={contentFormData.resource_data?.is_gated || false}
                                onChange={(e) => setContentFormData({ 
                                  ...contentFormData, 
                                  resource_data: { ...contentFormData.resource_data, is_gated: e.target.checked }
                                })}
                                className="rounded border-white/20"
                              />
                              Gated content (requires contact info)
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Form Actions */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                      <Button
                        type="button"
                        onClick={() => setShowContentForm(false)}
                        variant="ghost"
                        className="text-white/60"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628]"
                        data-testid="save-content-btn"
                      >
                        {loading ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        {selectedContent ? 'Update' : 'Create'} Content
                      </Button>
                    </div>
                  </form>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default AdminPage;
